/**
 * show-network
 * ------------
 * The server/client seam for show network + artwork data: the network-name precedence, and the
 * shape of the catalog series-details response that carries it. Dependency-free and PURE — no
 * React, no MUI, no env — so BOTH the Node-only catalog route and the client-side show carousel
 * can import it. It lives here rather than on the component because `server/` must never import
 * client code, and both sides referencing one declaration is what stops a rename from breaking
 * only one of them.
 */

/**
 * The one image the refreshed tile can use. Only the two url fields it actually reads are
 * declared — the feed also returns `width`/`height` (as STRINGS) and a `dominantColor`, none of
 * which any consumer touches, so they are deliberately not part of this contract.
 */
export interface SeriesDetailsImage {
  imageUrl: string;
  defaultImageUrl?: string;
}

/** The catalog series-details response, in full. */
export interface SeriesDetailsProjection {
  /** "" when the series carries no network at all — the tile then omits its second line. */
  network: string;
  /** null when no requested spec came back with a url, so the tile keeps its own image. */
  image: SeriesDetailsImage | null;
}

/**
 * Reads the same three fields the carousel's package filter matches on, and scans every
 * consumable as that filter does (its allow-set is an unordered Set, so it implies no
 * precedence — the order below is the display order the tile and the catalog route share).
 * Returns "" when the resource carries no network at all, which is the callers' cue to omit
 * the value rather than render an empty line.
 */
export const resolveNetworkName = (resource: any): string => {
  const attributed = resource?.networkAttributionName;
  if (typeof attributed === "string" && attributed) {
    return attributed;
  }
  const consumables = Array.isArray(resource?.consumables) ? resource.consumables : [];
  // Every candidate is narrowed to a non-empty string before it is returned: the payload is
  // untyped, and a non-string here would reach the tile as "Watch on [object Object]" and the
  // route's `network` as something its own declared type says it cannot be. A junk value is
  // treated as absent, so resolution falls through to the next field and then the next consumable.
  const named = (consumable: any): string => {
    const channel = consumable?.channel?.networkName;
    if (typeof channel === "string" && channel) {
      return channel;
    }
    const provider = consumable?.provider?.networkName;
    return typeof provider === "string" && provider ? provider : "";
  };
  return named(consumables.find((consumable: any) => named(consumable)));
};
